// Общее для аналитики админки: форматирование, сравнение периодов,
// палитра типов документов, мини-график и переключатель периода.
import React from "react";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL !== undefined
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:5001";

// проверено валидатором палитр: различимы при дальтонизме, контраст ≥ 3:1
export const TYPE_META = {
  passport: { label: "Паспорт", color: "#6E5BC4" },
  drivers_license: { label: "Права", color: "#2F8F5B" },
  civil: { label: "Документы РФ", color: "#2B7BB9" },
  contract: { label: "Договор", color: "#D0643B" },
  other: { label: "Другое", color: "#8A8C94" },
};
export const TYPE_ORDER = ["passport", "drivers_license", "civil", "contract", "other"];

export const MONEY = { revenue: "#16171B", ai: "#E0894F" };

export const STAGE_LABELS = {
  passport_extraction: "Извлечение: паспорт",
  dl_extraction: "Извлечение: права",
  civil_document_extraction: "Извлечение: документ РФ",
  contract_extraction: "Извлечение: договор",
  document_tree_extraction: "Структура документа",
  visual_translation: "Визуальный перевод",
  process_general: "Общая обработка",
  unknown: "Этап не указан",
};

export const TIME_STAGES = {
  prepare: { label: "Подготовка файла", color: "#8A8C94" },
  extraction: { label: "Распознавание", color: "#6E5BC4" },
  translation: { label: "Перевод", color: "#2B7BB9" },
  layout: { label: "Вёрстка", color: "#D0643B" },
  save: { label: "Сохранение", color: "#2F8F5B" },
};

/** 14 с · 1 мин 05 с; null → «—» */
export const secs = (ms) => {
  if (ms == null) return "—";
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total < 1 ? "<1" : total} с`;
  const m = Math.floor(total / 60);
  const sec = String(total % 60).padStart(2, "0");
  return `${m} мин ${sec} с`;
};

export const PERIODS = [
  { days: 7, label: "7 дней" },
  { days: 30, label: "30 дней" },
  { days: 90, label: "90 дней" },
  { days: 365, label: "Год" },
];

const nf = (digits) =>
  new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });

// копейки — всегда два знака, иначе «2,2 ₽» читается как опечатка
export const rub = (v, digits = 0) =>
  `${new Intl.NumberFormat("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v || 0)} ₽`;
export const num = (v) => nf(0).format(v || 0);

export const plural = (n, one, few, many) => {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
};

export const tzOffset = () => -new Date().getTimezoneOffset();

export const periodParams = (period) =>
  period.custom
    ? { start: period.custom.start, end: period.custom.end, tz_offset: tzOffset() }
    : { days: period.days, tz_offset: tzOffset() };

export const periodPhrase = (period, data) => {
  if (period.custom || !data) return "прошлым периодом";
  return `прошлыми ${data.period.days} ${plural(data.period.days, "днём", "днями", "днями")}`;
};

/**
 * Изменение к прошлому периоду. higherIsBetter=false — для затрат.
 * Возвращает { text, tone } или null, если сравнивать не с чем.
 */
export const delta = (cur, prev, { higherIsBetter = true, pp = false } = {}) => {
  if (cur == null || prev == null) return null;
  if (pp) {
    const d = Math.round((cur - prev) * 10) / 10;
    if (d === 0) return { text: "без изменений", tone: "flat" };
    return {
      text: `${d > 0 ? "▲ +" : "▼ −"}${String(Math.abs(d)).replace(".", ",")} п.п.`,
      tone: (d > 0) === higherIsBetter ? "good" : "bad",
    };
  }
  if (!prev) return cur ? { text: "▲ новое", tone: higherIsBetter ? "good" : "bad" } : null;
  const pct = Math.round(((cur - prev) / Math.abs(prev)) * 100);
  if (pct === 0) return { text: "без изменений", tone: "flat" };
  return {
    text: `${pct > 0 ? "▲ +" : "▼ −"}${Math.abs(pct)}%`,
    tone: (pct > 0) === higherIsBetter ? "good" : "bad",
  };
};

export const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "short" });

export const whenLabel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  if (d.toDateString() === today.toDateString()) return `сегодня, ${time}`;
  if (d.toDateString() === y.toDateString()) return `вчера, ${time}`;
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
};

export const Sparkline = ({ values, color = "#5B7FB8", width = 96, height = 32 }) => {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const points = values
    .map((v, i) => {
      const x = Math.round((i / (values.length - 1)) * width);
      const y = Math.round(height - 3 - (v / max) * (height - 6));
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const DeltaText = ({ d }) =>
  d ? <span className={`an-delta is-${d.tone}`}>{d.text}</span> : null;

export const PeriodSwitch = ({ period, onChange, allLabel }) => (
  <div className="an-seg" role="radiogroup" aria-label="Период">
    {PERIODS.map((p) => (
      <button
        key={p.days}
        type="button"
        role="radio"
        aria-checked={!period.custom && period.days === p.days}
        className={!period.custom && period.days === p.days ? "is-active" : ""}
        onClick={() => onChange({ days: p.days })}
      >
        {p.days === 365 && allLabel ? allLabel : p.label}
      </button>
    ))}
  </div>
);

export const ChartTip = ({ active, payload, label, rows }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-chart-tip an-tip">
      <div className="an-tip-title">{dayLabel(label)}</div>
      {rows(payload[0].payload).map(([name, value]) => (
        <div key={name} className="an-tip-row">
          <span>{name}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
};
